import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { stats } from "../../data/site";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  const target = parseFloat(value);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  const display = Number.isInteger(target) ? Math.round(n) : n.toFixed(1);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

export default function Stats() {
  return (
    <section
      data-testid="stats-section"
      className="relative py-20 md:py-28 border-t border-[#2E2E38] section-stripe overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-30 blur-3xl"
        style={{ background: "radial-gradient(ellipse, #4A1D6E 0%, transparent 60%)" }}
      />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="surface-card group p-8 md:p-12 hover:border-[#E3FF34]/50 hover:-translate-y-1 transition-all duration-500"
            >
              <div className="relative z-10 font-['Anton'] text-6xl md:text-7xl lg:text-8xl text-[#F2F2F2] leading-none group-hover:text-[#E3FF34] transition-colors duration-500">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <p className="relative z-10 mt-4 font-['JetBrains_Mono'] text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
